import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Calendar,
  ChevronDown,
  ChevronRight,
  Copy,
  Flame,
  LayoutGrid,
  List,
  Minus,
  Package,
  Phone,
  Plus,
  Search,
  Share2,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import toast from 'react-hot-toast'
import {
  buildCatalogShareMessage,
  buildWhatsAppAppointmentMessage,
  buildWhatsAppDirectOrderMessage,
  buildWhatsAppProductInquiryMessage,
  buildWhatsAppShareLink,
  calculateDiscountedPrice,
  formatMoney,
  getPublicCatalogPath,
  getPublicCatalogUrl,
} from '@/lib/catalogLinks'
import {
  getPublicCatalog,
  getPublicProductDetail,
  type PublicCatalogProduct,
  type PublicCatalogResponse,
  type PublicProductDetailResponse,
} from '@/services/publicCatalogApi'
import {
  normalizeToVertical,
  VerticalProductBadge,
  VerticalAvailabilityBadge,
} from '@/lib/verticalUI'

interface CartItem {
  product: PublicCatalogProduct
  quantity: number
}

function isAppointmentVertical(vertical: string): boolean {
  return ['salud', 'servicios', 'belleza_estetica', 'inmobiliaria', 'turismo_posadas'].includes(vertical)
}

function getSearchPlaceholder(vertical: string): string {
  switch (vertical) {
    case 'tienda_repuestos':
      return 'Buscar pieza, modelo de auto, marca o SKU...'
    case 'restaurante':
    case 'bodegon_licoreria':
      return 'Buscar plato, bebida, combo o licor...'
    case 'salud':
      return 'Buscar consulta, examen o servicio médico...'
    case 'servicios':
    case 'belleza_estetica':
      return 'Buscar servicio, tratamiento o especialista...'
    default:
      return 'Buscar producto, categoría o palabra clave...'
  }
}

export default function PublicCatalogPage() {
  const { tenantSlug = '', productSlug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const query = searchParams.get('q') ?? ''
  const categoryParam = searchParams.get('category') ?? ''
  const offersParam = searchParams.get('offers') === 'true'
  const sortParam = (searchParams.get('sort') as 'relevance' | 'price_asc' | 'price_desc' | 'featured') || 'relevance'

  const [catalog, setCatalog] = useState<PublicCatalogResponse | null>(null)
  const [productsList, setProductsList] = useState<PublicCatalogProduct[]>([])
  const [detail, setDetail] = useState<PublicProductDetailResponse | null>(null)
  const [loadingSummary, setLoadingSummary] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState(query)
  const [currentPage, setCurrentPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [isCartOpen, setIsCartOpen] = useState(false)

  // Cesta de compras en memoria sincronizada con sessionStorage por tienda
  const [cart, setCart] = useState<Record<string, CartItem>>(() => {
    try {
      if (typeof window === 'undefined') return {}
      const saved = sessionStorage.getItem(`cart_${tenantSlug}`)
      return saved ? JSON.parse(saved) : {}
    } catch {
      return {}
    }
  })

  // Sincronizar cesta con sessionStorage
  useEffect(() => {
    try {
      if (tenantSlug) {
        sessionStorage.setItem(`cart_${tenantSlug}`, JSON.stringify(cart))
      }
    } catch {
      // Ignorar quota errors
    }
  }, [cart, tenantSlug])

  // Sincronizar input con searchParams
  useEffect(() => {
    setSearchValue(query)
  }, [query])

  // Debounce para búsqueda reactiva
  useEffect(() => {
    const handler = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      const clean = searchValue.trim()
      if (clean) {
        if (next.get('q') !== clean) {
          next.set('q', clean)
          setSearchParams(next, { replace: true })
        }
      } else if (next.has('q')) {
        next.delete('q')
        setSearchParams(next, { replace: true })
      }
    }, 280)

    return () => clearTimeout(handler)
  }, [searchValue])

  // Carga inicial del catálogo
  useEffect(() => {
    let mounted = true

    const loadSummary = async () => {
      try {
        setLoadingSummary(true)
        setCurrentPage(1)
        const data = await getPublicCatalog(tenantSlug, {
          query,
          categorySlug: categoryParam,
          offersOnly: offersParam,
          sort: sortParam !== 'relevance' ? sortParam : undefined,
          page: 1,
        })
        if (!mounted) return
        setCatalog(data)
        setProductsList(data.products || [])
        setHasMore(Boolean(data.pagination?.hasMore))
        setError(null)
      } catch {
        if (!mounted) return
        setError('No pudimos cargar el catálogo. Intenta de nuevo más tarde.')
        setCatalog(null)
        setProductsList([])
      } finally {
        if (mounted) setLoadingSummary(false)
      }
    }

    if (tenantSlug) {
      loadSummary()
    }

    return () => {
      mounted = false
    }
  }, [tenantSlug, query, categoryParam, offersParam, sortParam])

  // Cargar más productos (paginación infinita / botón)
  const handleLoadMore = async () => {
    if (!catalog || loadingMore || !hasMore) return
    const nextPage = currentPage + 1

    try {
      setLoadingMore(true)
      const data = await getPublicCatalog(tenantSlug, {
        query,
        categorySlug: categoryParam,
        offersOnly: offersParam,
        sort: sortParam !== 'relevance' ? sortParam : undefined,
        page: nextPage,
      })
      setProductsList((prev) => [...prev, ...(data.products || [])])
      setCurrentPage(nextPage)
      setHasMore(Boolean(data.pagination?.hasMore))
    } catch {
      toast.error('Error al cargar más productos')
    } finally {
      setLoadingMore(false)
    }
  }

  // Carga de detalle de producto cuando la URL contiene productSlug
  useEffect(() => {
    let mounted = true

    const loadDetail = async () => {
      if (!tenantSlug || !productSlug) {
        setDetail(null)
        return
      }

      try {
        setLoadingDetail(true)
        const data = await getPublicProductDetail(tenantSlug, productSlug)
        if (!mounted) return
        setDetail(data)
      } catch {
        if (!mounted) return
        setDetail(null)
      } finally {
        if (mounted) setLoadingDetail(false)
      }
    }

    loadDetail()
    return () => {
      mounted = false
    }
  }, [tenantSlug, productSlug])

  // Normalización del rubro
  const verticalSlug = normalizeToVertical(catalog?.tenant?.businessType)
  const isAppointment = isAppointmentVertical(verticalSlug)

  // Categorías y jerarquía
  const categories = catalog?.categories ?? []

  // Categorías padre (sin parentId)
  const parentCategories = useMemo(() => {
    return categories.filter((c) => !c.parentId)
  }, [categories])

  // Encontrar la categoría activa (puede ser padre o hija)
  const activeCategoryObj = useMemo(() => {
    if (!categoryParam) return null
    return categories.find((c) => c.slug === categoryParam) ?? null
  }, [categories, categoryParam])

  // Categoría padre activa (si se seleccionó una hija o un padre)
  const activeParentCategory = useMemo(() => {
    if (!activeCategoryObj) return null
    if (!activeCategoryObj.parentId) return activeCategoryObj
    return categories.find((c) => c.id === activeCategoryObj.parentId) ?? activeCategoryObj
  }, [categories, activeCategoryObj])

  // Subcategorías de la categoría padre activa
  const subCategories = useMemo(() => {
    if (!activeParentCategory) return []
    return activeParentCategory.children ?? []
  }, [activeParentCategory])

  // Métricas de carrito
  const cartEntries = useMemo(() => Object.values(cart), [cart])
  const cartItemsCount = useMemo(() => {
    return cartEntries.reduce((sum, item) => sum + item.quantity, 0)
  }, [cartEntries])

  const cartTotalCents = useMemo(() => {
    return cartEntries.reduce((sum, item) => {
      const { finalPriceCents } = calculateDiscountedPrice(item.product.priceCents, item.product.promotion)
      return sum + finalPriceCents * item.quantity
    }, 0)
  }, [cartEntries])

  // Manejo de cantidades en cesta
  const addToCart = (product: PublicCatalogProduct, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setCart((prev) => {
      const existing = prev[product.id]
      const nextQuantity = (existing?.quantity ?? 0) + 1
      return {
        ...prev,
        [product.id]: { product, quantity: nextQuantity },
      }
    })
    toast.success(`${product.name} agregado`, { id: `add-${product.id}`, duration: 1500 })
  }

  const removeFromCart = (productId: string, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    setCart((prev) => {
      const existing = prev[productId]
      if (!existing) return prev
      if (existing.quantity <= 1) {
        const next = { ...prev }
        delete next[productId]
        return next
      }
      return {
        ...prev,
        [productId]: { ...existing, quantity: existing.quantity - 1 },
      }
    })
  }

  const deleteFromCart = (productId: string) => {
    setCart((prev) => {
      const next = { ...prev }
      delete next[productId]
      return next
    })
  }

  const clearCart = () => {
    setCart({})
  }

  // Navegación de filtros
  const handleCategorySelect = (slug: string) => {
    const next = new URLSearchParams(searchParams)
    if (slug) {
      next.set('category', slug)
    } else {
      next.delete('category')
    }
    setSearchParams(next, { replace: true })
  }

  const toggleOffers = () => {
    const next = new URLSearchParams(searchParams)
    if (offersParam) {
      next.delete('offers')
    } else {
      next.set('offers', 'true')
    }
    setSearchParams(next, { replace: true })
  }

  const handleSortChange = (sort: 'relevance' | 'price_asc' | 'price_desc' | 'featured') => {
    const next = new URLSearchParams(searchParams)
    if (sort !== 'relevance') {
      next.set('sort', sort)
    } else {
      next.delete('sort')
    }
    setSearchParams(next, { replace: true })
  }

  const clearAllFilters = () => {
    setSearchValue('')
    setSearchParams({}, { replace: true })
  }

  const hasActiveFilters = Boolean(query || categoryParam || offersParam || sortParam !== 'relevance')

  // Apertura y cierre del modal de producto
  const openProductDetail = (product: PublicCatalogProduct) => {
    const currentParams = searchParams.toString()
    const path = getPublicCatalogPath(tenantSlug, product.slug)
    navigate(`${path}${currentParams ? `?${currentParams}` : ''}`)
  }

  const closeProductDetail = () => {
    const currentParams = searchParams.toString()
    const path = getPublicCatalogPath(tenantSlug)
    navigate(`${path}${currentParams ? `?${currentParams}` : ''}`)
  }

  // Compartir catálogo general
  const handleShareCatalog = async () => {
    if (!catalog) return
    const url = getPublicCatalogUrl(tenantSlug)
    if (navigator.share) {
      try {
        await navigator.share({
          title: catalog.tenant.name,
          text: `Mira el catálogo oficial de ${catalog.tenant.name}`,
          url,
        })
        return
      } catch {
        // Fallback a portapapeles
      }
    }
    await navigator.clipboard.writeText(url)
    toast.success('Enlace del catálogo copiado')
  }

  // Compartir producto individual
  const handleShareProduct = async (product: PublicCatalogProduct, event?: React.MouseEvent) => {
    if (event) event.stopPropagation()
    const url = getPublicCatalogUrl(tenantSlug, product.slug)
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.name,
          text: `Mira ${product.name} en ${catalog?.tenant.name}`,
          url,
        })
        return
      } catch {
        // Fallback
      }
    }
    await navigator.clipboard.writeText(url)
    toast.success('Enlace del producto copiado')
  }

  // Contactar al agente / WhatsApp de la tienda
  const handleContactWhatsApp = (product?: PublicCatalogProduct) => {
    if (!catalog) return
    const tenantName = catalog.tenant.name
    const whatsappPhone = catalog.tenant.whatsappPhone || undefined
    const url = getPublicCatalogUrl(tenantSlug, product?.slug)

    let message = ''

    if (isAppointment && product) {
      const { finalPriceCents } = calculateDiscountedPrice(product.priceCents, product.promotion)
      const priceLabel = formatMoney(finalPriceCents, product.currency)
      message = buildWhatsAppAppointmentMessage({
        tenantName,
        serviceName: product.name,
        priceLabel,
        url,
      })
    } else if (product) {
      const { finalPriceCents } = calculateDiscountedPrice(product.priceCents, product.promotion)
      const priceLabel = formatMoney(finalPriceCents, product.currency)
      message = buildWhatsAppProductInquiryMessage({
        tenantName,
        productName: product.name,
        priceLabel,
        url,
      })
    } else {
      message = buildCatalogShareMessage({
        tenantName,
        url,
      })
    }

    const shareUrl = buildWhatsAppShareLink(message, whatsappPhone)
    window.open(shareUrl, '_blank', 'noopener,noreferrer')
  }

  // Enviar pedido consolidado de la cesta por WhatsApp
  const handleSendCartOrder = () => {
    if (!catalog || cartEntries.length === 0) return
    const whatsappPhone = catalog.tenant.whatsappPhone || undefined
    const url = getPublicCatalogUrl(tenantSlug)

    const items = cartEntries.map((item) => {
      const { finalPriceCents } = calculateDiscountedPrice(item.product.priceCents, item.product.promotion)
      return {
        name: item.product.name,
        quantity: item.quantity,
        priceCents: finalPriceCents,
        currency: item.product.currency,
        sku: item.product.sku,
      }
    })

    const message = buildWhatsAppDirectOrderMessage({
      tenantName: catalog.tenant.name,
      items,
      url,
    })

    const shareUrl = buildWhatsAppShareLink(message, whatsappPhone)
    window.open(shareUrl, '_blank', 'noopener,noreferrer')
    setIsCartOpen(false)
  }

  const activeProduct = detail?.product ?? productsList.find((p) => p.slug === productSlug) ?? null

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 antialiased selection:bg-emerald-500 selection:text-white">
      {/* Fondo degradado moderno */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(16,185,129,0.12),rgba(255,255,255,0))]" />

      <div className="relative mx-auto flex min-h-screen w-full max-w-7xl flex-col px-3 py-4 sm:px-6 lg:px-8">
        {/* ========================================================= */}
        {/* HEADER COMPACTO CON IDENTIDAD DE MARCA */}
        {/* ========================================================= */}
        <header className="rounded-3xl border border-emerald-100/80 bg-white/95 p-4 shadow-sm backdrop-blur-md sm:p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3.5">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20 sm:h-14 sm:w-14">
                <ShoppingBag className="h-6 w-6 sm:h-7 sm:w-7" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                    <Sparkles className="h-3 w-3" />
                    Catálogo oficial
                  </span>
                  {catalog?.tenant?.businessType && (
                    <span className="hidden text-xs text-slate-400 sm:inline">• {catalog.tenant.businessType}</span>
                  )}
                </div>
                <h1 className="truncate text-xl font-black tracking-tight text-slate-900 sm:text-2xl">
                  {catalog?.tenant?.name || tenantSlug || 'Catálogo'}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleShareCatalog}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 active:scale-95 sm:text-sm"
              >
                <Share2 className="h-4 w-4 text-slate-500" />
                <span className="hidden xs:inline">Compartir</span>
              </button>

              <button
                type="button"
                onClick={() => handleContactWhatsApp()}
                className="inline-flex items-center gap-2 rounded-full bg-[#075f55] px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-[#064e46] active:scale-95 sm:text-sm"
              >
                <Phone className="h-3.5 w-3.5" />
                <span>WhatsApp</span>
              </button>

              {/* Botón Carrito en Header (solo rubros de producto) */}
              {!isAppointment && (
                <button
                  type="button"
                  onClick={() => setIsCartOpen(true)}
                  className="relative inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 shadow-sm transition hover:bg-slate-50"
                  aria-label="Ver cesta"
                >
                  <ShoppingCart className="h-5 w-5" />
                  {cartItemsCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1 text-[11px] font-black text-white shadow-sm">
                      {cartItemsCount}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Quick info bar debajo del header */}
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
            <span>
              <strong>{catalog?.stats?.totalProducts ?? productsList.length}</strong> productos
            </span>
            <span>•</span>
            <span>
              <strong>{catalog?.stats?.totalCategories ?? categories.length}</strong> categorías
            </span>
            {(catalog?.stats?.totalFeaturedProducts ?? 0) > 0 && (
              <>
                <span>•</span>
                <span className="text-amber-700">
                  <strong>{catalog?.stats?.totalFeaturedProducts}</strong> destacados
                </span>
              </>
            )}
          </div>
        </header>

        {/* ========================================================= */}
        {/* BARRA STICKY DE BÚSQUEDA Y CATEGORÍAS */}
        {/* ========================================================= */}
        <section className="sticky top-2 z-20 mt-4 space-y-2.5 rounded-3xl border border-slate-200/80 bg-white/95 p-3 shadow-md backdrop-blur-md sm:p-4">
          {/* Fila 1: Input de búsqueda y switch de vista */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder={getSearchPlaceholder(verticalSlug)}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50/80 py-2.5 pl-10 pr-9 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => setSearchValue('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  aria-label="Limpiar búsqueda"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Selector de ordenamiento */}
            <div className="relative">
              <select
                value={sortParam}
                onChange={(e) => handleSortChange(e.target.value as any)}
                className="cursor-pointer appearance-none rounded-2xl border border-slate-200 bg-slate-50 py-2.5 pl-3 pr-8 text-xs font-semibold text-slate-700 outline-none transition hover:bg-slate-100 focus:border-emerald-400 sm:text-sm"
              >
                <option value="relevance">Relevancia</option>
                <option value="price_asc">Menor precio</option>
                <option value="price_desc">Mayor precio</option>
                <option value="featured">Destacados</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Switch Grid / List */}
            <div className="flex rounded-2xl border border-slate-200 bg-slate-100 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`rounded-xl p-1.5 transition ${viewMode === 'grid' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                aria-label="Vista cuadrícula"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded-xl p-1.5 transition ${viewMode === 'list' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-700'}`}
                aria-label="Vista lista"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Fila 2: Chips de categorías Nivel 1 */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            <button
              type="button"
              onClick={() => handleCategorySelect('')}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold transition active:scale-95 ${
                !categoryParam && !offersParam
                  ? 'bg-emerald-600 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Todos
            </button>

            {/* Chip de Ofertas */}
            <button
              type="button"
              onClick={toggleOffers}
              className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold transition active:scale-95 ${
                offersParam
                  ? 'bg-amber-500 text-slate-950 shadow-xs'
                  : 'border border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100'
              }`}
            >
              <Flame className="h-3.5 w-3.5" />
              Solo Ofertas
            </button>

            {parentCategories.map((cat) => {
              const isSelected = activeParentCategory?.id === cat.id && !offersParam
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => handleCategorySelect(cat.slug)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold transition active:scale-95 ${
                    isSelected
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {cat.name}
                </button>
              )
            })}
          </div>

          {/* Fila 3: Chips de Subcategorías (Nivel 2) si la categoría activa tiene hijas */}
          {subCategories.length > 0 && !offersParam && (
            <div className="flex items-center gap-1.5 overflow-x-auto border-t border-slate-100 pt-2 scrollbar-none">
              <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Subcategorías:
              </span>
              <button
                type="button"
                onClick={() => handleCategorySelect(activeParentCategory?.slug || '')}
                className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                  categoryParam === activeParentCategory?.slug
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Todas en {activeParentCategory?.name}
              </button>
              {subCategories.map((sub) => (
                <button
                  key={sub.id}
                  type="button"
                  onClick={() => handleCategorySelect(sub.slug)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                    categoryParam === sub.slug
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}

          {/* Barra de filtros activos */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2 text-xs text-slate-500">
              <div className="flex flex-wrap items-center gap-1.5">
                <span>Filtros activos:</span>
                {query && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-800">
                    "{query}"
                    <button type="button" onClick={() => setSearchValue('')}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {categoryParam && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-800">
                    {activeCategoryObj?.name || categoryParam}
                    <button type="button" onClick={() => handleCategorySelect('')}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {offersParam && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 font-semibold text-amber-900">
                    🔥 Ofertas
                    <button type="button" onClick={toggleOffers}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={clearAllFilters}
                className="font-bold text-emerald-600 hover:underline"
              >
                Limpiar filtros
              </button>
            </div>
          )}
        </section>

        {/* ========================================================= */}
        {/* LISTADO DE PRODUCTOS */}
        {/* ========================================================= */}
        <main className="mt-4 flex-1">
          {error ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
              <p className="font-semibold">{error}</p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="mt-3 rounded-full bg-red-600 px-4 py-2 text-xs font-bold text-white hover:bg-red-700"
              >
                Reintentar
              </button>
            </div>
          ) : loadingSummary ? (
            <div
              className={`grid gap-3 sm:gap-4 ${
                viewMode === 'grid'
                  ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                  : 'grid-cols-1'
              }`}
            >
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="h-72 animate-pulse rounded-3xl bg-slate-200/70" />
              ))}
            </div>
          ) : productsList.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-12 text-center shadow-xs">
              <ShoppingBag className="mx-auto h-12 w-12 text-slate-300" />
              <h3 className="mt-3 text-base font-bold text-slate-900">No encontramos productos</h3>
              <p className="mt-1 text-sm text-slate-500">
                Prueba buscando con otros términos o seleccionando otra categoría.
              </p>
              <button
                type="button"
                onClick={clearAllFilters}
                className="mt-4 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-xs hover:bg-emerald-700"
              >
                Ver todos los productos
              </button>
            </div>
          ) : (
            <>
              {/* Grilla o Lista */}
              <div
                className={`grid gap-3 sm:gap-4 ${
                  viewMode === 'grid'
                    ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4'
                    : 'grid-cols-1'
                }`}
              >
                {productsList.map((product) => {
                  const { finalPriceCents, originalPriceCents, hasDiscount, discountLabel } = calculateDiscountedPrice(
                    product.priceCents,
                    product.promotion
                  )
                  const cartItem = cart[product.id]
                  const inCartCount = cartItem?.quantity ?? 0

                  if (viewMode === 'list') {
                    // Vista Lista Compacta (ideal para Restaurante, Autopartes, Salud)
                    return (
                      <article
                        key={product.id}
                        onClick={() => openProductDetail(product)}
                        className="group flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-slate-200/90 bg-white p-3 shadow-xs transition hover:border-emerald-300 hover:shadow-md"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="relative h-18 w-18 shrink-0 overflow-hidden rounded-xl bg-slate-100 sm:h-20 sm:w-20">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt={product.name}
                                className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                loading="lazy"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-300">
                                <Package className="h-8 w-8" />
                              </div>
                            )}
                            {hasDiscount && (
                              <span className="absolute left-1 top-1 rounded-md bg-amber-400 px-1 py-0.5 text-[9px] font-black text-slate-950">
                                {discountLabel}
                              </span>
                            )}
                          </div>

                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
                              <span className="truncate">{product.category?.name || 'General'}</span>
                              {product.sku && <span>• {product.sku}</span>}
                            </div>
                            <h3 className="truncate text-sm font-bold text-slate-900 sm:text-base">{product.name}</h3>
                            <VerticalProductBadge metadata={product.metadata} vertical={verticalSlug} />
                            <div className="mt-1 flex items-baseline gap-2">
                              <span className="text-sm font-black text-slate-950 sm:text-base">
                                {formatMoney(finalPriceCents, product.currency)}
                              </span>
                              {hasDiscount && (
                                <span className="text-xs text-slate-400 line-through">
                                  {formatMoney(originalPriceCents, product.currency)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Botones de acción en lista */}
                        <div className="flex shrink-0 items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {isAppointment ? (
                            <button
                              type="button"
                              onClick={() => handleContactWhatsApp(product)}
                              className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 active:scale-95"
                            >
                              Consultar
                            </button>
                          ) : inCartCount > 0 ? (
                            <div className="flex items-center gap-1 rounded-xl border border-emerald-300 bg-emerald-50 p-1">
                              <button
                                type="button"
                                onClick={(e) => removeFromCart(product.id, e)}
                                className="rounded-lg bg-white p-1 text-slate-700 shadow-xs hover:bg-slate-100"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-5 text-center text-xs font-black text-emerald-800">{inCartCount}</span>
                              <button
                                type="button"
                                onClick={(e) => addToCart(product, e)}
                                className="rounded-lg bg-emerald-600 p-1 text-white shadow-xs hover:bg-emerald-700"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => addToCart(product, e)}
                              className="inline-flex items-center gap-1 rounded-xl bg-slate-900 px-3 py-2 text-xs font-bold text-white shadow-xs hover:bg-emerald-600 active:scale-95"
                            >
                              <Plus className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Agregar</span>
                            </button>
                          )}
                        </div>
                      </article>
                    )
                  }

                  // Vista Grilla 2 Columnas (Mobile-First)
                  return (
                    <article
                      key={product.id}
                      onClick={() => openProductDetail(product)}
                      className="group flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-slate-200/90 bg-white shadow-xs transition duration-200 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-lg"
                    >
                      {/* Imagen Aspect Square */}
                      <div className="relative aspect-square w-full overflow-hidden bg-slate-100">
                        {product.imageUrl ? (
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-300">
                            <Package className="h-12 w-12" />
                          </div>
                        )}

                        {/* Badges superiores */}
                        <div className="absolute left-2 top-2 flex flex-col gap-1">
                          {hasDiscount && (
                            <span className="rounded-lg bg-amber-400 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-950 shadow-xs">
                              {discountLabel}
                            </span>
                          )}
                          {product.isFeatured && (
                            <span className="rounded-lg bg-slate-900/80 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur-xs">
                              ⭐ Top
                            </span>
                          )}
                        </div>

                        {/* Botón rápido de compartir */}
                        <button
                          type="button"
                          onClick={(e) => handleShareProduct(product, e)}
                          className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-slate-600 shadow-sm backdrop-blur-xs transition hover:bg-white hover:text-emerald-700"
                          title="Compartir producto"
                        >
                          <Share2 className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* Info del Producto */}
                      <div className="flex flex-1 flex-col justify-between p-3 sm:p-4">
                        <div>
                          <div className="flex items-center justify-between gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                            <span className="truncate">{product.category?.name || 'General'}</span>
                            <VerticalAvailabilityBadge stock={product.stock} vertical={verticalSlug} />
                          </div>
                          <h3 className="mt-1 line-clamp-2 text-xs font-bold leading-snug text-slate-900 sm:text-sm">
                            {product.name}
                          </h3>
                          <div className="mt-1">
                            <VerticalProductBadge metadata={product.metadata} vertical={verticalSlug} />
                          </div>
                        </div>

                        <div className="mt-3">
                          {/* Precios */}
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-sm font-black text-slate-950 sm:text-lg">
                              {formatMoney(finalPriceCents, product.currency)}
                            </span>
                            {hasDiscount && (
                              <span className="text-[11px] text-slate-400 line-through sm:text-xs">
                                {formatMoney(originalPriceCents, product.currency)}
                              </span>
                            )}
                          </div>

                          {/* Botón de acción */}
                          <div className="mt-2.5" onClick={(e) => e.stopPropagation()}>
                            {isAppointment ? (
                              <button
                                type="button"
                                onClick={() => handleContactWhatsApp(product)}
                                className="w-full rounded-2xl bg-emerald-600 py-2 text-xs font-bold text-white shadow-xs transition hover:bg-emerald-700 active:scale-95"
                              >
                                Solicitar cita
                              </button>
                            ) : inCartCount > 0 ? (
                              <div className="flex items-center justify-between rounded-2xl border border-emerald-300 bg-emerald-50 px-2 py-1">
                                <button
                                  type="button"
                                  onClick={(e) => removeFromCart(product.id, e)}
                                  className="rounded-xl bg-white p-1 text-slate-700 shadow-xs hover:bg-slate-100"
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </button>
                                <span className="text-xs font-black text-emerald-800">{inCartCount} en pedido</span>
                                <button
                                  type="button"
                                  onClick={(e) => addToCart(product, e)}
                                  className="rounded-xl bg-emerald-600 p-1 text-white shadow-xs hover:bg-emerald-700"
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => addToCart(product, e)}
                                className="flex w-full items-center justify-center gap-1.5 rounded-2xl bg-slate-900 py-2 text-xs font-bold text-white shadow-xs transition hover:bg-emerald-600 active:scale-95"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Agregar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </article>
                  )
                })}
              </div>

              {/* Botón Cargar Más */}
              {hasMore && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-xs font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    {loadingMore ? 'Cargando más productos...' : 'Cargar más productos'}
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* ========================================================= */}
      {/* MODAL / BOTTOM SHEET DE DETALLE DE PRODUCTO */}
      {/* ========================================================= */}
      {productSlug && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/60 p-0 backdrop-blur-xs sm:items-center sm:p-4">
          <div
            className="fixed inset-0"
            onClick={closeProductDetail}
            aria-label="Cerrar modal"
          />

          <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl">
            {/* Header del modal */}
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white/95 px-5 py-4 backdrop-blur-md">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Detalle del producto</span>
              <button
                type="button"
                onClick={closeProductDetail}
                className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {loadingDetail ? (
              <div className="p-8 text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
                <p className="mt-3 text-xs text-slate-500">Cargando información del producto...</p>
              </div>
            ) : activeProduct ? (
              <div className="p-5 sm:p-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  {/* Imagen */}
                  <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-slate-100">
                    {activeProduct.imageUrl ? (
                      <img
                        src={activeProduct.imageUrl}
                        alt={activeProduct.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-300">
                        <Package className="h-16 w-16" />
                      </div>
                    )}
                    {activeProduct.promotion && (
                      <div className="absolute left-3 top-3 rounded-lg bg-amber-400 px-2.5 py-1 text-xs font-black text-slate-950 shadow-sm">
                        {calculateDiscountedPrice(activeProduct.priceCents, activeProduct.promotion).discountLabel}
                      </div>
                    )}
                  </div>

                  {/* Datos del producto */}
                  <div className="flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between text-xs font-semibold text-slate-400">
                        <span>{activeProduct.category?.name || 'General'}</span>
                        {activeProduct.sku && <span>SKU: {activeProduct.sku}</span>}
                      </div>

                      <h2 className="mt-2 text-xl font-black text-slate-900 sm:text-2xl">{activeProduct.name}</h2>

                      <div className="mt-3 flex items-baseline gap-2">
                        {(() => {
                          const { finalPriceCents, originalPriceCents, hasDiscount } = calculateDiscountedPrice(
                            activeProduct.priceCents,
                            activeProduct.promotion
                          )
                          return (
                            <>
                              <span className="text-2xl font-black text-slate-950">
                                {formatMoney(finalPriceCents, activeProduct.currency)}
                              </span>
                              {hasDiscount && (
                                <span className="text-sm text-slate-400 line-through">
                                  {formatMoney(originalPriceCents, activeProduct.currency)}
                                </span>
                              )}
                            </>
                          )
                        })()}
                      </div>

                      <div className="mt-2">
                        <VerticalAvailabilityBadge stock={activeProduct.stock} vertical={verticalSlug} />
                      </div>

                      <div className="mt-3">
                        <VerticalProductBadge metadata={activeProduct.metadata} vertical={verticalSlug} />
                      </div>

                      <p className="mt-4 text-xs leading-relaxed text-slate-600 sm:text-sm">
                        {activeProduct.description || 'Sin descripción adicional disponible.'}
                      </p>
                    </div>

                    {/* Acciones principales */}
                    <div className="mt-6 flex flex-col gap-2">
                      {isAppointment ? (
                        <button
                          type="button"
                          onClick={() => handleContactWhatsApp(activeProduct)}
                          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#075f55] py-3 text-sm font-bold text-white shadow-md transition hover:bg-[#064e46]"
                        >
                          <Calendar className="h-4 w-4" />
                          Consultar disponibilidad / Solicitar cita
                        </button>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => addToCart(activeProduct)}
                            className="flex flex-1 items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-3 text-sm font-bold text-white shadow-md transition hover:bg-emerald-700"
                          >
                            <Plus className="h-4 w-4" />
                            Agregar al pedido
                          </button>
                          <button
                            type="button"
                            onClick={() => handleContactWhatsApp(activeProduct)}
                            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-700 shadow-sm transition hover:bg-slate-50"
                            title="Preguntar por WhatsApp"
                          >
                            <Phone className="h-4 w-4 text-[#075f55]" />
                          </button>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={() => handleShareProduct(activeProduct)}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        <Copy className="h-3.5 w-3.5" />
                        Copiar enlace del producto
                      </button>
                    </div>
                  </div>
                </div>

                {/* Relacionados */}
                {detail?.relatedProducts && detail.relatedProducts.length > 0 && (
                  <div className="mt-8 border-t border-slate-100 pt-6">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">
                      Opciones relacionadas
                    </h4>
                    <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {detail.relatedProducts.map((rel) => (
                        <button
                          key={rel.id}
                          type="button"
                          onClick={() => openProductDetail(rel)}
                          className="flex flex-col rounded-2xl border border-slate-200 p-2.5 text-left transition hover:border-emerald-400 hover:bg-emerald-50/30"
                        >
                          <span className="line-clamp-1 text-xs font-bold text-slate-800">{rel.name}</span>
                          <span className="mt-1 text-xs font-black text-emerald-700">
                            {formatMoney(rel.priceCents, rel.currency)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <p>No se encontró el producto solicitado.</p>
                <button
                  type="button"
                  onClick={closeProductDetail}
                  className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white"
                >
                  Volver al catálogo
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* BARRA FLOTANTE DE CESTA (MÓVIL) */}
      {/* ========================================================= */}
      {!isAppointment && cartItemsCount > 0 && (
        <div className="fixed bottom-4 inset-x-3 z-40 sm:hidden">
          <div className="flex items-center justify-between rounded-2xl bg-slate-950 p-3 text-white shadow-2xl ring-1 ring-white/10">
            <button
              type="button"
              onClick={() => setIsCartOpen(true)}
              className="flex items-center gap-2.5 text-left"
            >
              <div className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <ShoppingCart className="h-5 w-5" />
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-400 px-1 text-[9px] font-black text-slate-950">
                  {cartItemsCount}
                </span>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-slate-400">{cartItemsCount} en tu lista</p>
                <p className="text-sm font-black text-white">{formatMoney(cartTotalCents, 'USD')}</p>
              </div>
            </button>

            <button
              type="button"
              onClick={handleSendCartOrder}
              className="inline-flex items-center gap-1.5 rounded-xl bg-[#075f55] px-4 py-2.5 text-xs font-bold text-white shadow-md active:scale-95"
            >
              <span>Pedir por WhatsApp</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* ========================================================= */}
      {/* DRAWER LATERAL DE CESTA / PEDIDO RÁPIDO */}
      {/* ========================================================= */}
      {isCartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/60 backdrop-blur-xs">
          <div className="fixed inset-0" onClick={() => setIsCartOpen(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
            {/* Header del Drawer */}
            <div className="flex items-center justify-between border-b border-slate-100 p-4">
              <div className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-emerald-600" />
                <h3 className="text-base font-black text-slate-900">Tu pedido para WhatsApp</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsCartOpen(false)}
                className="rounded-full bg-slate-100 p-1.5 text-slate-500 hover:bg-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Lista de productos en cesta */}
            <div className="flex-1 overflow-y-auto p-4">
              {cartEntries.length === 0 ? (
                <div className="py-12 text-center text-slate-400">
                  <ShoppingCart className="mx-auto h-12 w-12 text-slate-200" />
                  <p className="mt-2 text-sm font-bold text-slate-700">Tu selección está vacía</p>
                  <p className="text-xs text-slate-500">Agrega productos del catálogo para armar tu pedido.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cartEntries.map(({ product, quantity }) => {
                    const { finalPriceCents } = calculateDiscountedPrice(product.priceCents, product.promotion)
                    const itemTotal = finalPriceCents * quantity

                    return (
                      <div
                        key={product.id}
                        className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3"
                      >
                        <div className="min-w-0 flex-1">
                          <h4 className="truncate text-xs font-bold text-slate-900 sm:text-sm">{product.name}</h4>
                          <p className="text-xs text-slate-500">
                            {formatMoney(finalPriceCents, product.currency)} c/u
                          </p>
                          <p className="text-xs font-black text-slate-900">
                            Subtotal: {formatMoney(itemTotal, product.currency)}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
                            <button
                              type="button"
                              onClick={() => removeFromCart(product.id)}
                              className="rounded-lg bg-white p-1 text-slate-700 shadow-xs hover:bg-slate-100"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <span className="w-5 text-center text-xs font-bold text-slate-900">{quantity}</span>
                            <button
                              type="button"
                              onClick={() => addToCart(product)}
                              className="rounded-lg bg-emerald-600 p-1 text-white shadow-xs hover:bg-emerald-700"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={() => deleteFromCart(product.id)}
                            className="rounded-lg p-1.5 text-slate-400 hover:text-red-600"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Footer de la Cesta */}
            {cartEntries.length > 0 && (
              <div className="border-t border-slate-100 bg-slate-50 p-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Total estimado:</span>
                  <span className="text-xl font-black text-slate-950">{formatMoney(cartTotalCents, 'USD')}</span>
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Al presionar el botón se abrirá WhatsApp con el pedido listo para que el agente confirme stock y pago.
                </p>

                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={handleSendCartOrder}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#075f55] py-3.5 text-sm font-bold text-white shadow-md transition hover:bg-[#064e46]"
                  >
                    <Phone className="h-4 w-4" />
                    Enviar pedido por WhatsApp
                  </button>
                  <button
                    type="button"
                    onClick={clearCart}
                    className="text-center text-xs font-semibold text-slate-500 hover:text-slate-800"
                  >
                    Vaciar lista
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
